import React from "react";
import { Link, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowDown, Calendar, Split, Route, ShieldCheck, Check } from "lucide-react";
import CustomBlocks from "@/components/site/CustomBlocks";

const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0 } };
const featIcons = [Calendar, Split, Route, ShieldCheck];

export default function Home({ previewSite }) {
  const outlet = useOutletContext();
  const site = previewSite || outlet?.site;
  if (!site) return null;
  const p = site.pages.home;

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-20 grid lg:grid-cols-[1.05fr_0.95fr] gap-14 items-center">
        <motion.div initial="hidden" animate="show" variants={fadeUp} transition={{ duration: 0.7 }}>
          <p className="uppercase tracking-[0.2em] text-xs font-bold text-[#9A8C73] mb-6">{p.hero.eyebrow}</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1.02] text-[#1A201D] mb-7">
            {p.hero.title}
          </h1>
          <p className="text-base md:text-lg text-[#4A5550] leading-relaxed max-w-xl mb-9">{p.hero.subtitle}</p>
          <div className="flex flex-wrap gap-4">
            <Link to={p.hero.cta_primary_href || "/register"} data-testid="hero-cta-primary"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#2C3531] text-[#FDFBF7] font-semibold hover:scale-105 transition-transform duration-300">
              {p.hero.cta_primary} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to={p.hero.cta_secondary_href || "/how-it-works"} data-testid="hero-cta-secondary"
              className="inline-flex items-center px-7 py-4 rounded-full border border-[#2C3531]/25 text-[#2C3531] font-semibold hover:bg-[#F3EFE7] transition-colors">
              {p.hero.cta_secondary}
            </Link>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.15 }} className="relative">
          <div className="overflow-hidden rounded-[3rem] rounded-tr-lg shadow-[0_20px_60px_rgba(44,53,49,0.15)]">
            <img src={p.hero.image} alt="" className="w-full h-[300px] sm:h-[440px] lg:h-[560px] object-cover" />
          </div>
          <div className="absolute -bottom-6 -left-4 sm:left-6 bg-[#FDFBF7] border border-[#E5DFD3] rounded-2xl px-6 py-4 shadow-[0_12px_40px_rgba(44,53,49,0.10)]">
            <p className="font-display text-3xl font-semibold text-[#2C3531]">$0</p>
            <p className="text-xs text-[#4A5550] uppercase tracking-wider">Ad spend required</p>
          </div>
        </motion.div>
      </section>

      {/* PROBLEM */}
      <section className="bg-[#F3EFE7] py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <p className="uppercase tracking-[0.2em] text-xs font-bold text-[#9A8C73] mb-4">{p.problem.eyebrow}</p>
          <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#1A201D] max-w-2xl mb-6">{p.problem.title}</h2>
          <p className="text-[#4A5550] max-w-2xl leading-relaxed mb-14">{p.problem.intro}</p>
          <div className="grid md:grid-cols-3 gap-6">
            {p.problem.items.map((it, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="bg-[#FDFBF7] border border-[#E5DFD3] rounded-2xl p-8">
                <span className="font-display text-4xl font-light text-[#9A8C73]">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-bold text-lg mt-4 mb-2 text-[#1A201D]">{it.title}</h3>
                <p className="text-sm text-[#4A5550] leading-relaxed">{it.desc}</p>
              </motion.div>
            ))}
          </div>
          <p className="mt-12 font-display text-2xl md:text-3xl font-medium text-[#2C3531] max-w-3xl leading-snug">{p.problem.punchline}</p>
        </div>
      </section>

      {/* SOLUTION + FLOW */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-28">
        <div className="max-w-2xl mb-16">
          <p className="uppercase tracking-[0.2em] text-xs font-bold text-[#9A8C73] mb-4">{p.solution.eyebrow}</p>
          <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#1A201D] mb-6">{p.solution.title}</h2>
          <p className="text-[#4A5550] leading-relaxed rte-content" dangerouslySetInnerHTML={{ __html: p.solution.body }} />
        </div>

        <div className="grid lg:grid-cols-[auto_1fr] gap-10 items-center">
          <div className="flex flex-col items-center gap-4">
            <div className="bg-[#2C3531] text-[#FDFBF7] rounded-2xl px-8 py-6 text-center min-w-[220px]">
              <Calendar className="w-6 h-6 mx-auto mb-2 text-[#9A8C73]" strokeWidth={1.5} />
              <p className="font-semibold">{p.solution.master_label}</p>
            </div>
            <ArrowDown className="w-6 h-6 text-[#9A8C73]" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full">
              {p.solution.nodes.map((n, i) => (
                <div key={i} className="bg-[#FDFBF7] border border-[#E5DFD3] rounded-2xl px-5 py-5 text-center">
                  <p className="font-semibold text-[#1A201D]">{n.label}</p>
                  <p className="text-xs text-[#9A8C73] mt-1 font-semibold">{n.desc}</p>
                </div>
              ))}
            </div>
          </div>
          <ol className="space-y-5 lg:pl-10 lg:border-l border-[#E5DFD3]">
            {p.solution.steps.map((s, i) => (
              <li key={i} className="flex gap-4">
                <span className="shrink-0 w-8 h-8 rounded-full bg-[#9A8C73]/15 text-[#7A6F5B] font-bold text-sm flex items-center justify-center">{i + 1}</span>
                <p className="text-[#4A5550] leading-relaxed pt-1">{s}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* BUILT FOR */}
      <section className="bg-[#2C3531] text-[#FDFBF7] py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <p className="uppercase tracking-[0.2em] text-xs font-bold text-[#9A8C73] mb-4">{p.built_for.eyebrow}</p>
          <h2 className="font-display text-4xl md:text-5xl font-semibold max-w-2xl mb-14">{p.built_for.title}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {p.built_for.items.map((it, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="border border-[#FDFBF7]/15 rounded-2xl p-6 hover:bg-[#FDFBF7]/5 transition-colors">
                <h3 className="font-bold mb-2">{it.title}</h3>
                <p className="text-sm text-[#FDFBF7]/65 leading-relaxed">{it.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES bento */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-28">
        <p className="uppercase tracking-[0.2em] text-xs font-bold text-[#9A8C73] mb-4">{p.features.eyebrow}</p>
        <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#1A201D] max-w-2xl mb-14">{p.features.title}</h2>
        <div className="grid md:grid-cols-12 gap-6">
          {p.features.items.map((it, i) => {
            const Icon = featIcons[i % featIcons.length];
            const span = i % 4 === 0 || i % 4 === 3 ? "md:col-span-7" : "md:col-span-5";
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className={`${span} bg-[#F3EFE7] border border-[#E5DFD3] rounded-2xl p-9`}>
                <Icon className="w-8 h-8 text-[#9A8C73] mb-5" strokeWidth={1.5} />
                <h3 className="font-display text-2xl font-semibold text-[#1A201D] mb-2">{it.title}</h3>
                <p className="text-[#4A5550] leading-relaxed">{it.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-28">
        <div className="bg-[#2C3531] rounded-[2.5rem] px-8 md:px-16 py-16 md:py-20 text-center relative overflow-hidden">
          <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#FDFBF7] mb-4 max-w-2xl mx-auto">{p.cta.title}</h2>
          <p className="text-[#FDFBF7]/70 mb-9 max-w-xl mx-auto">{p.cta.subtitle}</p>
          <Link to={p.cta.button_href || "/register"} data-testid="home-cta-bottom"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#9A8C73] text-white font-semibold hover:scale-105 transition-transform duration-300">
            {p.cta.button} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <CustomBlocks blocks={p.blocks} />
    </div>
  );
}
